import React from 'react';
import AppRecovery from './AppRecovery';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
  errorStack: string | null;
}

/**
 * AppBootErrorBoundary — top-level error guard for the entire authenticated
 * dashboard subtree. Catches any synchronous render error escaping from
 * <AppLayout> (FamilyProvider, FamilyGraphSection, ResilienceMonitor,
 * QuickAddModal, etc.) and replaces the blank screen with the universal
 * <AppRecovery /> "We're reconnecting your family access" surface.
 *
 * NEVER mounts inside a provider that itself might crash — it MUST be the
 * outermost client wrapper around AppLayout in src/pages/Index.tsx.
 *
 * Pure additive. Does NOT touch:
 *   - relationship engine
 *   - spouse / in-law logic
 *   - tree layout
 *   - uploads / media pipeline
 *   - billing / Stripe / FamousPay
 *   - Supabase schema / RLS
 *   - existing sessions
 */
class AppBootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '', errorStack: null };
  }

  static getDerivedStateFromError(err: unknown): State {
    const message =
      err instanceof Error && err.message
        ? err.message
        : 'Unknown render error during app bootstrap.';
    const stack =
      err instanceof Error && err.stack ? err.stack.slice(0, 1500) : null;
    return { hasError: true, message, errorStack: stack };
  }

  componentDidCatch(err: unknown, info: unknown) {
    // Internal-only diagnostics — never shown to the user except behind
    // the collapsed "Technical details" disclosure on AppRecovery.
    // eslint-disable-next-line no-console
    console.error('[KinsCircle][BootBoundary] caught render error', {
      err,
      info,
      authUid:
        typeof window !== 'undefined'
          ? (() => {
              try {
                const raw = window.localStorage.getItem('sb-auth-token');
                return raw ? '<present>' : '<none>';
              } catch {
                return '<unreadable>';
              }
            })()
          : '<ssr>',
      hostname:
        typeof window !== 'undefined' ? window.location.hostname : '<ssr>',
      pathname:
        typeof window !== 'undefined' ? window.location.pathname : '<ssr>',
    });
  }

  handleRetry = () => {
    // First try a soft reset — clearing the error state lets the same
    // subtree re-render. If the underlying mapping has now resolved (e.g.
    // a slow profile fetch finally landed) the user is back in business
    // without a hard reload.
    this.setState({ hasError: false, message: '', errorStack: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const diagnostic = [
      this.state.message,
      this.state.errorStack ? `\n${this.state.errorStack}` : '',
    ]
      .filter(Boolean)
      .join('');

    return (
      <AppRecovery
        diagnostic={diagnostic || null}
        onRetry={this.handleRetry}
      />
    );
  }
}

export default AppBootErrorBoundary;
